export interface ReloadGuardOptions {
    /** Whether `navigator.serviceWorker.controller` was non-null when the page loaded. */
    hadController: boolean;
    reload: () => void;
    /** When true at the moment a reload is due (e.g. a sign-in is in flight), the reload is deferred via `onIdle`. */
    isBusy?: () => boolean;
    /** Registers `resume`, to be called once the page is no longer busy. Called at most once per guard. */
    onIdle?: ( resume: () => void ) => void;
}

/**
 * Reload EVERY client when a new service worker takes control, not just the tab that clicked "Update now":
 * a second window (or the standalone PWA window) would otherwise keep executing the old bundle against a
 * database the new bundle has since upgraded. The first install (no prior controller) must not reload
 * unless the user asked, and a second `controllerchange` must not reload twice. A reload that lands while
 * `isBusy()` is deferred to `onIdle` — still at most one reload in total.
 */
export function createReloadGuard( { hadController, reload, isBusy, onIdle }: ReloadGuardOptions ) {
    let userRequested = false;
    let refreshing = false;
    let reloaded = false;
    const reloadOnce = (): void => {
        if ( reloaded ) return;
        reloaded = true;
        reload();
    };
    return {
        userRequestedUpdate(): void { userRequested = true; },
        onControllerChange(): void {
            if ( refreshing || !( hadController || userRequested ) ) return;
            refreshing = true;
            if ( isBusy?.() && onIdle ) {
                onIdle( reloadOnce );
                return;
            }
            reloadOnce();
        }
    };
}
